import { ChangeDetectionStrategy, Component, Input, OnInit, inject, signal, Signal, TemplateRef, ChangeDetectorRef  } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, timer, merge, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SelectRolDialogComponent } from '@app/shared/components/dialogs/select-rol/select-rol.component';
import { MatDividerModule } from '@angular/material/divider';

import { User } from '@app/models/usuarios/user.model';
import { Liga } from '@app/models/liga.model';
import { ContextoBackend } from '@app/models/auth/contexto-backend';
import { EscuelasService } from '@app/services/escuelas/escuelas.service';
import { WaSaldo } from '@app/models/escuelas/escuela.model';

// Importamos el servicio de sesión y la interfaz de usuario
import {
  ServicioSesion
} from '@app/services/sesion/sesion.service';
import { ServicioBarraSuperior } from '@app/services/general/barra-superior.service';
import { SubdominioService } from '@app/services/general/subdominio.service';
import { ThemingService } from '@app/services/general/theming.service';

// Importamos los módulos de Angular Material que necesitamos
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-barra-superior',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatDialogModule,
  ],
  templateUrl: './barra-superior.component.html',
  styleUrls: ['./barra-superior.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarraSuperiorComponent implements OnInit {
  @Input() liga: Liga | null = null; 
  public servicioSesion = inject(ServicioSesion);
  private router = inject(Router);
  private _servicioBarra = inject(ServicioBarraSuperior); 
  private readonly _dialog = inject(MatDialog);
  private readonly _escuelasService = inject(EscuelasService);
  private readonly _subdominioService = inject(SubdominioService);
  private readonly _theming = inject(ThemingService);

  public modoOscuro = signal<boolean>(this._theming.esModoOscuroActivo());

  public usuario$: Observable<User | null>;
  public rolActivo$ = this.servicioSesion.rolActivo$;
  private _cdr = inject(ChangeDetectorRef);

  public waSaldo$: Observable<WaSaldo | null> = this.servicioSesion.rolActivo$.pipe(
    switchMap(rol => {
      if (rol?.tipo !== 'EQUIPO' || !rol.equipoId) return of(null);
      return merge(timer(0, 300_000), this._servicioBarra.refreshWaSaldo$).pipe(
        switchMap(() => this._escuelasService.getWaSaldo(rol.equipoId!).pipe(
          catchError(() => of(null))
        ))
      );
    })
  );


  // Revisa cada 5 min si el backend cambió los permisos del rol/equipo activo
  // (p.ej. le quitaron 'eliminar-staff-equipo' a un rol mientras el usuario
  // sigue con la sesión abierta). switchContext actualiza usuarioActual y
  // HasPermissionDirective oculta/muestra los botones reactivamente.
  private readonly _refrescoPermisos$ = this.servicioSesion.rolActivo$.pipe(
    switchMap(rol => {
      if (!rol) return of(null);
      return timer(300_000, 300_000).pipe(
        switchMap(() => this.servicioSesion.refrescarPermisosContextoActual().pipe(
          catchError(() => of(null))
        ))
      );
    }),
    takeUntilDestroyed()
  ).subscribe();

  public tituloActual: Signal<string> = this._servicioBarra.tituloActual;
  public iconoSvgTitulo: Signal<string | null> = this._servicioBarra.iconoSvgTitulo;
  public contenidoPersonalizado: Signal<TemplateRef<any> | null> = this._servicioBarra.contenidoPersonalizado;
  public accionAtras: Signal<(() => void) | null> = this._servicioBarra.accionAtras;

  constructor() {
    this.usuario$ = this.servicioSesion.usuarioActual$;

  }

  ngOnInit(): void {
  }

  public cerrarSesion(): void {
    this.servicioSesion.cerrarSesion();
    this.router.navigate(['/login']);
  }

  public toggleMenu(): void {
    this._servicioBarra.toggleMenuLateral();
  }

  // El toggle flotante del menú lateral se oculta en móvil/tablet (<1024px,
  // ver barra-lateral.component.scss) — este es el único acceso a modo
  // oscuro/claro que queda visible en esos anchos.
  public toggleModoOscuro(): void {
    this._theming.alternarModoOscuro();
    this.modoOscuro.set(this._theming.esModoOscuroActivo());
  }

  public cambiarPerfil(): void {
    const contextos = this.servicioSesion.getContextos();
    if (!contextos.length) return;

    const dialogRef = this._dialog.open(SelectRolDialogComponent, {
      width: '400px',
      data: { contextos }
    });

    dialogRef.afterClosed().subscribe((contextoElegido: ContextoBackend) => {
      if (contextoElegido) {
        this.servicioSesion.switchContext(contextoElegido).subscribe({
          next: () => {
            this._cdr.markForCheck();
            this._subdominioService.redirigirSegunContexto(contextoElegido);
          }
        });
      }
    });
  }

public getWaClase(saldo: WaSaldo): string {
    const total = saldo.wa_monthly_limit + saldo.wa_extra_balance;
    if (total === 0) return '';
    const pct = (saldo.wa_current_usage / total) * 100;
    if (pct >= 100) return 'wa-rojo';
    if (pct >= 90) return 'wa-naranja';
    if (pct >= 70) return 'wa-amarillo';
    return '';
  }


}
